import { useMemo, useState } from 'react';
import {
  categories,
  featureStories,
  categoryFeed,
  type CategoryKey,
  type NewsItem,
} from '@/mocks/gossip';

function readsItem(n: NewsItem) {
  return (
    <>
      <span className="whitespace-nowrap">{n.time}</span>
      <span className="whitespace-nowrap">
        <i className="ri-eye-line mr-1 align-[-1px]" />
        {n.reads}
      </span>
    </>
  );
}

export default function TopStories({ query }: { query: string }) {
  const [active, setActive] = useState<CategoryKey | 'all'>('all');

  const allFeeds = useMemo(() => {
    const list: (NewsItem & { catLabel: string })[] = [];
    Object.keys(categoryFeed).forEach((k) => {
      const ck = k as CategoryKey;
      const meta = categories.find((c) => c.key === ck);
      categoryFeed[ck].forEach((it) => list.push({ ...it, catLabel: meta?.label ?? '' }));
    });
    return list;
  }, []);

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allFeeds.filter(
      (it) => it.title.toLowerCase().includes(q) || it.excerpt.toLowerCase().includes(q)
    );
  }, [query, allFeeds]);

  const currentFeed =
    searchResults.length > 0
      ? searchResults
      : active === 'all'
      ? allFeeds
      : allFeeds.filter((it) => it.category === active);

  const currentLabel = searchResults.length > 0 ? `“${query}”的搜索结果` : '圈子爆料';

  return (
    <>
      {/* 今日热闻 */}
      <section id="hot" className="mx-auto w-full max-w-[1280px] scroll-mt-24 px-4 py-16 md:px-6">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="flex items-center gap-2 text-sm font-medium text-primary-500">
              <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              TODAY'S TOP
            </p>
            <h2 className="mt-2 font-heading text-3xl font-black tracking-tight text-foreground-950">
              今日热闻
            </h2>
          </div>
          <a href="#rank" className="hidden shrink-0 items-center gap-1 text-sm text-foreground-600 hover:text-primary-600 sm:flex">
            查看完整榜单
            <i className="ri-arrow-right-line" />
          </a>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <a href="#rank" className="group lg:col-span-2">
            <div className="relative overflow-hidden rounded-lg">
              <img
                src={featureStories[0].image}
                alt={featureStories[0].title}
                className="h-[260px] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] md:h-[360px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute left-0 right-0 bottom-0 p-5 md:p-6">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-accent-500 px-2 py-1 text-xs font-medium text-white">
                    {featureStories[0].marker}
                  </span>
                  <span className="text-xs text-white/80">{featureStories[0].tag}</span>
                </div>
                <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-white md:text-2xl">
                  {featureStories[0].title}
                </h3>
                <p className="mt-2 hidden text-sm leading-relaxed text-white/85 md:line-clamp-2 md:block">
                  {featureStories[0].excerpt}
                </p>
              </div>
            </div>
          </a>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-1">
            {featureStories.slice(1).map((it) => (
              <a href="#rank" key={it.id} className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200">
                <img
                  src={it.image}
                  alt={it.title}
                  className="h-[84px] w-[120px] shrink-0 rounded-md object-cover"
                />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-primary-500">{it.tag}</p>
                  <h3 className="mt-1 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-900 group-hover:text-primary-600">
                    {it.title}
                  </h3>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* 圈子爆料 + 分类筛选 */}
      <section id="paoliao" className="scroll-mt-24 bg-background-100/70">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 text-sm font-medium text-accent-600">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
                INSIDER FEED
              </p>
              <h2 className="mt-2 font-heading text-3xl font-black tracking-tight text-foreground-950">
                {currentLabel}
              </h2>
            </div>
            {searchResults.length > 0 && (
              <a
                href="#paoliao"
                className="flex shrink-0 items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
              >
                清除搜索
                <i className="ri-close-line" />
              </a>
            )}
          </div>

          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            {searchResults.length > 0
              ? `为你找到 ${searchResults.length} 条相关圈内消息。`
              : '按圈层浏览内部人士与知情人士分享的最新见闻，点击标签可快速筛选你感兴趣的领域。'}
          </p>

          {searchResults.length === 0 && (
            <div className="scrollbar-thin mt-7 flex gap-2 overflow-x-auto pb-1">
              <button
                type="button"
                onClick={() => setActive('all')}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  active === 'all'
                    ? 'bg-foreground-950 text-background-50'
                    : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-foreground-400'
                }`}
              >
                全部
              </button>
              {categories.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setActive(c.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                    active === c.key
                      ? 'bg-foreground-950 text-background-50'
                      : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-foreground-400'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}

          {currentFeed.length > 0 ? (
            <div className="mt-7 grid grid-cols-1 gap-5 lg:grid-cols-2">
              {currentFeed.map((it) => (
                <a
                  href="#contact"
                  key={it.id}
                  className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-accent-300 sm:flex-row"
                >
                  <img
                    src={it.image}
                    alt={it.title}
                    className="h-40 w-full shrink-0 rounded-md object-cover sm:h-[132px] sm:w-[200px]"
                  />
                  <div className="flex min-w-0 flex-col">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="whitespace-nowrap rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">
                        {it.catLabel}
                      </span>
                      <span className="whitespace-nowrap text-xs font-medium text-primary-600">
                        {it.tag}
                      </span>
                    </div>
                    <h3 className="mt-2 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-900 group-hover:text-primary-600">
                      {it.title}
                    </h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                      {it.excerpt}
                    </p>
                    <div className="mt-auto flex flex-wrap items-center gap-3 pt-3 text-xs text-foreground-400">
                      {readsItem(it)}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-50 p-12 text-center">
              <i className="ri-inbox-2-line text-4xl text-foreground-300" />
              <p className="mt-4 font-heading text-lg font-bold text-foreground-800">暂时没有相关内容</p>
              <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或回到全部栏目继续浏览。</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}